import type { AreaDoc } from '@talkofthetown/shared';
import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';

/**
 * Live subscriptions to a set of area boards. Each viewer reads only these few small documents;
 * a board update costs one read per viewer, however many posts it contains.
 */
export function useAreaDocs(keys: readonly string[]): { docs: AreaDoc[]; loaded: boolean } {
  const [byKey, setByKey] = useState<Record<string, AreaDoc | null>>({});
  const signature = keys.join(',');

  useEffect(() => {
    const unsubs = signature
      .split(',')
      .filter(Boolean)
      .map((key) =>
        onSnapshot(
          doc(db, 'areas', key),
          (snap) =>
            setByKey((prev) => ({
              ...prev,
              [key]: snap.exists() ? (snap.data() as AreaDoc) : null,
            })),
          (err) => {
            console.warn(`Board ${key} unavailable`, err);
            setByKey((prev) => ({ ...prev, [key]: null }));
          },
        ),
      );
    return () => unsubs.forEach((u) => u());
  }, [signature]);

  return useMemo(() => {
    const docs = keys.map((k) => byKey[k]).filter((d): d is AreaDoc => !!d);
    return { docs, loaded: keys.every((k) => k in byKey) };
  }, [byKey, keys]);
}
